import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { BlockUI, NgBlockUI } from 'ng-block-ui';
import {
  MatchDayOption,
  SummaryOperationResponse,
  SummaryStatus,
  SummaryStatusResponse,
  SummaryType,
} from '../../models/round-summary.model';
import { MatchesService } from '../../services/matches.service';
import { GroupsService } from '../../services/groups.service';
import { RoundSummariesService } from '../../services/round-summaries.service';
import { SnackbarService } from '../../services/snackbar.service';
import { ConfirmModalComponent } from '../shared/confirm-modal/confirm-modal.component';

@Component({
  selector: 'app-round-summaries',
  templateUrl: './round-summaries.component.html',
  styleUrls: ['./round-summaries.component.scss'],
  standalone: false,
})
export class RoundSummariesComponent implements OnInit {
  @BlockUI() blockUI!: NgBlockUI;

  mode: SummaryType = 'round';
  rounds: Array<{ name: string; title: string }> = [];
  matchDays: MatchDayOption[] = [];
  groups: any[] = [];
  selectedId = '';
  selectedGroupId = '';
  summary: SummaryStatusResponse | null = null;
  loadingStatus = false;

  constructor(
    private matchesService: MatchesService,
    private groupsService: GroupsService,
    private summariesService: RoundSummariesService,
    private snackbar: SnackbarService,
    private dialog: MatDialog,
    private router: Router,
  ) {}

  async ngOnInit(): Promise<void> {
    this.blockUI.start();
    try {
      [this.rounds, this.matchDays, this.groups] = await Promise.all([
        this.matchesService.getRounds(),
        this.summariesService.getMatchDays(),
        this.groupsService.getList(),
      ]);
      this.selectDefault();
      await this.loadStatus();
    } catch (error) {
      this.snackbar.error(this.getErrorMessage(error));
    } finally {
      this.blockUI.stop();
    }
  }

  goBack(): void {
    this.router.navigate(['/admin']);
  }

  async setMode(mode: SummaryType): Promise<void> {
    if (this.mode === mode) return;
    this.mode = mode;
    this.summary = null;
    this.selectedGroupId = '';
    this.selectDefault();
    await this.loadStatus();
  }

  async onSelectionChange(): Promise<void> {
    this.summary = null;
    await this.loadStatus();
  }

  async onGroupChange(): Promise<void> {
    this.summary = null;
    await this.loadStatus();
  }

  async loadStatus(): Promise<void> {
    if (!this.selectedId) return;
    this.loadingStatus = true;
    try {
      this.summary = await this.summariesService.getStatus(
        this.mode,
        this.selectedId,
        this.activeGroupId,
      );
    } catch (error) {
      this.summary = null;
      this.snackbar.error(this.getErrorMessage(error));
    } finally {
      this.loadingStatus = false;
    }
  }

  confirmGenerate(): void {
    const force = this.hasGeneratedSummary;
    this.dialog
      .open(ConfirmModalComponent, {
        panelClass: 'confirm-dialog',
        data: {
          title: force ? 'Regenerar resumen' : 'Generar resumen',
          eyebrow: this.mode === 'round' ? 'Resumen de ronda' : 'Resumen diario',
          icon: force ? 'refresh' : 'auto_awesome',
          tone: force ? 'danger' : 'highlight',
          message:
            `Se ${force ? 'regenerarán' : 'generarán'} las estadísticas y el texto con IA de ` +
            `<strong>${this.selectedTitle}</strong>${this.scopeDescription}. ` +
            'Todavía no se enviará ningún email.',
          cancelText: 'Cancelar',
          confirmText: force ? 'Regenerar' : 'Generar con IA',
        },
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) void this.generate(force);
      });
  }

  confirmGenerateAll(): void {
    const totalSummaries = this.groups.length + 1;
    this.dialog
      .open(ConfirmModalComponent, {
        panelClass: 'confirm-dialog',
        data: {
          title: 'Generar todos los resúmenes',
          eyebrow: 'Resumen diario',
          icon: 'auto_awesome',
          tone: 'highlight',
          message:
            `Se generarán de una vez los <strong>${totalSummaries} resúmenes</strong> de ` +
            `<strong>${this.selectedTitle}</strong>: la clasificación general y ` +
            `los ${this.groups.length} grupos. Los resúmenes que ya existan no se regenerarán ` +
            'y todavía no se enviará ningún email.',
          cancelText: 'Cancelar',
          confirmText: 'Generar todos',
        },
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) void this.generateAll();
      });
  }

  confirmSend(): void {
    const force = this.summary?.status === 'sent';
    this.dialog
      .open(ConfirmModalComponent, {
        panelClass: 'confirm-dialog',
        data: {
          title: force ? 'Reenviar resumen' : 'Enviar resumen',
          eyebrow: 'Envío de email',
          icon: 'outgoing_mail',
          tone: force ? 'danger' : 'highlight',
          message:
            `Se enviará el resumen ya generado de <strong>${this.selectedTitle}</strong> ` +
            `${this.sendAudience}.`,
          cancelText: 'Cancelar',
          confirmText: force ? 'Reenviar' : 'Enviar',
        },
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) void this.send(force);
      });
  }

  get selectedTitle(): string {
    if (this.mode === 'round') {
      return (
        this.rounds.find((round) => round.name === this.selectedId)?.title ??
        this.selectedId
      );
    }
    return (
      this.matchDays.find((day) => day.dayId === this.selectedId)?.title ??
      this.selectedId
    );
  }

  get selectedDay(): MatchDayOption | undefined {
    return this.matchDays.find((day) => day.dayId === this.selectedId);
  }

  get selectedGroup(): any | undefined {
    return this.groups.find((group) => group._id === this.selectedGroupId);
  }

  get activeGroupId(): string | undefined {
    return this.mode === 'match_day' && this.selectedGroupId
      ? this.selectedGroupId
      : undefined;
  }

  get scopeDescription(): string {
    return this.selectedGroup
      ? ` para el grupo <strong>${this.selectedGroup.name}</strong>`
      : '';
  }

  get sendAudience(): string {
    return this.selectedGroup
      ? `a los miembros de <strong>${this.selectedGroup.name}</strong>`
      : 'a todos los participantes';
  }

  get hasGeneratedSummary(): boolean {
    return !!this.summary?.data;
  }

  get canSend(): boolean {
    return this.hasGeneratedSummary && this.summary?.status !== 'pending';
  }

  get statusLabel(): string {
    const labels: Record<SummaryStatus, string> = {
      not_generated: 'No generado',
      pending: 'Pendiente',
      generated: 'Generado',
      sent: 'Enviado',
      error: 'Error',
    };
    return this.summary ? labels[this.summary.status] : 'Sin datos';
  }

  private selectDefault(): void {
    if (this.mode === 'round') {
      this.selectedId = this.rounds[0]?.name ?? '';
      return;
    }

    const lastClosed = [...this.matchDays].reverse().find((day) => day.closed);
    this.selectedId = lastClosed?.dayId ?? this.matchDays[0]?.dayId ?? '';
  }

  private async generate(force: boolean): Promise<void> {
    this.blockUI.start('Generando estadísticas y texto con IA...');
    try {
      const result = await this.summariesService.generate(
        this.mode,
        this.selectedId,
        force,
        this.activeGroupId,
      );
      this.showResult(result);
      await this.loadStatus();
    } catch (error) {
      this.snackbar.error(this.getErrorMessage(error));
    } finally {
      this.blockUI.stop();
    }
  }

  private async generateAll(): Promise<void> {
    const scopes: Array<string | undefined> = [
      undefined,
      ...this.groups.map((group) => group._id),
    ];
    let generated = 0;
    let alreadyGenerated = 0;
    let failed = 0;

    this.blockUI.start('Generando el resumen general y los de todos los grupos...');
    try {
      for (const groupId of scopes) {
        try {
          const result = await this.summariesService.generate(
            'match_day',
            this.selectedId,
            false,
            groupId,
          );

          if (result.status === 'generated') {
            generated++;
          } else if (result.status === 'already_generated') {
            alreadyGenerated++;
          } else {
            failed++;
          }
        } catch {
          failed++;
        }
      }

      this.showBulkGenerationResult(generated, alreadyGenerated, failed);
      await this.loadStatus();
    } finally {
      this.blockUI.stop();
    }
  }

  private async send(force: boolean): Promise<void> {
    this.blockUI.start('Enviando resumen...');
    try {
      const result = await this.summariesService.send(
        this.mode,
        this.selectedId,
        force,
        this.activeGroupId,
      );
      this.showResult(result);
      await this.loadStatus();
    } catch (error) {
      this.snackbar.error(this.getErrorMessage(error));
    } finally {
      this.blockUI.stop();
    }
  }

  private showBulkGenerationResult(
    generated: number,
    alreadyGenerated: number,
    failed: number,
  ): void {
    const details = [
      `${generated} generados`,
      `${alreadyGenerated} ya existentes`,
      `${failed} con error`,
    ].join(', ');

    if (failed === 0) {
      this.snackbar.success(`Generación completada: ${details}.`, 5000);
    } else if (generated > 0 || alreadyGenerated > 0) {
      this.snackbar.warning(`Generación parcial: ${details}.`, 6000);
    } else {
      this.snackbar.error(`No se pudo generar ningún resumen: ${details}.`, 6000);
    }
  }

  private showResult(result: SummaryOperationResponse): void {
    switch (result.status) {
      case 'generated':
        this.snackbar.success('Resumen generado correctamente.');
        break;
      case 'sent':
        this.snackbar.success('Resumen enviado correctamente.');
        break;
      case 'already_generated':
        this.snackbar.info('El resumen ya está generado.');
        break;
      case 'already_sent':
        this.snackbar.info('El resumen ya se había enviado.');
        break;
      case 'not_generated':
        this.snackbar.warning('Primero debes generar el resumen.');
        break;
      case 'period_not_closed':
        this.snackbar.warning(
          'El periodo no está cerrado: deben finalizar todos sus partidos.',
        );
        break;
      case 'error':
        this.snackbar.error(result.error || 'No se pudo completar la operación.');
        break;
    }
  }

  private getErrorMessage(error: unknown): string {
    if (
      error &&
      typeof error === 'object' &&
      'message' in error &&
      typeof error.message === 'string'
    ) {
      return error.message;
    }
    return 'No se pudo completar la operación.';
  }
}
